import React, { useEffect } from 'react';
import { Link as RouterLink, useParams, useHistory } from 'react-router-dom';
import { Box, Typography, Breadcrumbs, Link, Paper } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { CustomInput } from '../../../../components/CustomInput';
import { CustomInputWithMask } from '../../../../components/CustomInputWithMask';
import { CustomSelect } from '../../../../components/CustomSelect';
import { CustomInputTextArea } from '../../../../components/CustomInputTextArea';
import { CustomButton } from '../../../../components/CustomButton';
import { CustomDatePicker } from '../../../../components/CustomDatePicker';
import { ROUTES } from '../../../../constants/routes';
import { postCreateApplication } from '../../../../store/support/actions';
import { getSearchCustomer } from '../../../../store/support/actions';
import { useSearchCustomerBy } from '../../../../hooks/useSearchCustomerBy';
import { PERMISSIONS, REASON_FOR_CHARGE_ID, SUBSCRIBER_STATUS } from '../../../../constants/variables';
import { visibleBySectionPermission } from '../../../../utils/visibleBySectionPermission';
import { setUsersPermissions } from '../../../../utils/setUsersPermissions';

const mainStyleInput = {
  variant: 'outlined',
  size: 'small',
  sx: { width: 240 }
};

const validationSchema = yup.object({
  sla: yup.date().required(),
  subscriber_status: yup.object().required(),
  reason_for_charge_id: yup.object().required(),
  amount: yup.number().required(),
  description: yup.string().max(1000).required()
});

export const CreateApplication = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const { getParams, createPath } = useSearchCustomerBy();

  const { customer } = useSelector((state) => state.support);
  const { userPermissions, currentUsersPermissions } = useSelector((state) => state.userPermissions);
  const permissions = setUsersPermissions(userPermissions, currentUsersPermissions?.permissions);

  const redirectToApplications = () => history.push(ROUTES.APPLICATIONS);

  useEffect(() => {
    if (!Object.values(customer).length) dispatch(getSearchCustomer(getParams(params)));
  }, // eslint-disable-next-line
    []);

  const { handleSubmit, values, handleChange, errors, setFieldValue, resetForm, isValid, touched } =
    useFormik({
      initialValues: {
        sla: '',
        mobile_phone: customer?.mobile_phone,
        name: customer?.name,
        subscriber_status: '',
        reason_for_charge_id: '',
        amount: '',
        description: ''
      },
      validationSchema: validationSchema,
      validateOnChange: true,

      onSubmit: (values) => {
        if (isValid) {
          const settingsValues = {
            ...values,
            amount: values.amount,
            reason_for_charge_id: values.reason_for_charge_id.value,
            subscriber_status: values.subscriber_status.value,
            customer_id: customer?.personal_accounts?.[0].id,
            subscriber_iin: customer.iin
          };
          dispatch(postCreateApplication(settingsValues, redirectToApplications));
          resetForm();
        }
      }
    });

  const registerInput = (name, select) => ({
    name: name,
    value: values[name],
    error: touched[name] && errors[name],
    onChange: (e) => (select === 'select' ? handleChange : handleChange(e)),
    ...mainStyleInput
  });

  return (
    <Box>
      <Typography mb="24px" variant="h1" sx={{ color: 'text.primary' }} component="h1">
        Создать заявку
      </Typography>
      <Breadcrumbs mb="24px" aria-label="breadcrumb">
        <Link component={RouterLink} underline="hover" color="inherit" to={ROUTES.SUPPORT}>
          Техподдержка
        </Link>
        { customer?.name && (
          <Link
            component={RouterLink}
            underline="hover"
            color="inherit"
            to={createPath(ROUTES.SUPPORT_USER_CARD) || ROUTES.SUPPORT_USER_CARD}>
            {customer.name}
          </Link>
        )}
        <Typography color="text.secondary">Создать заявку</Typography>
      </Breadcrumbs>

      <Paper
        sx={{
          padding: '24px'
        }}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ width: 480 }}>
            <CustomInput
              disabled
              {...mainStyleInput}
              sx={{ width: 496, marginBottom: '24px' }}
              placeholder={customer?.name}
            />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gridColumnGap: '16px',
                gridRowGap: '24px'
              }}>
              <CustomInputWithMask
                disabled
                {...mainStyleInput}
                name='mobile_phone'
                value={customer?.mobile_phone?.toString()}
              />

              <CustomInput disabled {...mainStyleInput} placeholder="4567890-7" />

              <CustomDatePicker
                {...registerInput('sla')}
                setFieldValue={setFieldValue}
                placeholder="SLA"
              />

              <CustomSelect
                {...registerInput('subscriber_status', 'select')}
                placeholder="Статус клиента"
                setFieldValue={setFieldValue}
                options={SUBSCRIBER_STATUS}
              />

              <CustomSelect
                {...registerInput('reason_for_charge_id', 'select')}
                placeholder="Причина начисления"
                setFieldValue={setFieldValue}
                options={REASON_FOR_CHARGE_ID}
              />

              <CustomInput
                {...registerInput('amount')}
                type="number"
                placeholder="Количество бонусов"
              />
            </Box>

            <CustomInputTextArea
              {...registerInput('description')}
              style={{ width: 496, height: 120, marginTop: '24px' }}
              placeholder="Описание поступившей проблемы"
            />

            <Typography color="text.secondary">До 1000 символов.</Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '24px',
              ...visibleBySectionPermission(permissions, PERMISSIONS.SUPPORT.CreateSupportBonusRequests.value)
            }}
          >
            <CustomButton
              size="large"
              color="secondary"
              variant="contained"
              component={RouterLink}
              to={ROUTES.SUPPORT}
              data-testid="CancelBtn"
            >
              Отмена
            </CustomButton>

            <CustomButton
              type="submit"
              sx={{ marginLeft: '16px' }}
              size="large"
              variant="contained"
              data-testid="CreateBtn"
            >
              Создать
            </CustomButton>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};
